/**
 * Ticket WK4, Schritt 4: Bearbeiten direkt in der Zeile.
 *
 * Beim Überfahren erscheint ein kleiner Stift im Feld selbst; ein Klick macht
 * genau dieses Feld beschreibbar. Gespeichert wird mit der Eingabetaste und
 * automatisch beim Verlassen des Feldes (Klick daneben, Tab), die Escape-Taste
 * verwirft. Geschrieben wird über denselben Weg wie im Prüfeingang und in der
 * Detailansicht (saveMunicipality).
 */

import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import { saveMunicipality } from "@/lib/wahlkalender.functions";
import { RUNS_AGAIN_LABELS, RUNS_AGAIN_VALUES, type RunsAgain } from "@/lib/wahlkalender";
import {
  formatDateParts,
  joinHolderName,
  parseDateInput,
  splitHolderName,
} from "@/lib/wahlkalender-inline";
import {
  valuesFromRow,
  payloadFromValues,
  type MunicipalityEditValues,
} from "@/components/admin/WahlkalenderMunicipalityEdit";
import type { MunicipalityRow } from "@/lib/wahlkalender.server";

export type InlineField = "holder" | "termStart" | "confirmed" | "runsAgain";

/** Angezeigter Text eines bearbeitbaren Feldes. */
export function inlineDisplay(row: MunicipalityRow, field: InlineField): string {
  if (field === "holder") return joinHolderName(row) || "—";
  if (field === "termStart")
    return formatDateParts({
      year: row.termStartYear,
      month: row.termStartMonth,
      day: row.termStartDay,
    });
  if (field === "confirmed")
    return formatDateParts({
      year: row.confirmedYear,
      month: row.confirmedMonth,
      day: row.confirmedDay,
    });
  return RUNS_AGAIN_LABELS[row.runsAgain];
}

/** Rohtext im Eingabefeld, bevor jemand tippt. */
export function inlineInputValue(row: MunicipalityRow, field: InlineField): string {
  if (field === "holder") return joinHolderName(row);
  const shown = inlineDisplay(row, field);
  return shown === "—" ? "" : shown;
}

/**
 * Aus der Eingabe werden die Werte des gemeinsamen Schreibwegs gebildet.
 * „ungueltig" meldet eine abgewiesene Eingabe, bevor gespeichert wird.
 */
export function inlineValues(
  row: MunicipalityRow,
  field: InlineField,
  input: string,
): MunicipalityEditValues | { error: string } {
  const base = valuesFromRow(row);
  if (field === "holder") {
    const { firstName, lastName } = splitHolderName(input);
    return { ...base, firstName: firstName ?? "", lastName: lastName ?? "" };
  }
  if (field === "runsAgain") {
    if (!RUNS_AGAIN_VALUES.includes(input as RunsAgain))
      return { error: "Unbekannte Angabe zu „tritt erneut an“." };
    return { ...base, runsAgain: input as RunsAgain };
  }
  const parsed = parseDateInput(input);
  if (parsed === "ungueltig")
    return { error: "Bitte ein Datum wie 15.03.2026 oder 03/2026 eingeben." };
  if (field === "termStart") {
    if (parsed === null) return { error: "Der Amtsantritt darf nicht leer sein." };
    return {
      ...base,
      termStartYear: String(parsed.year),
      termStartMonth: String(parsed.month),
      termStartDay: parsed.day === null ? "" : String(parsed.day),
    };
  }
  return {
    ...base,
    confirmedYear: parsed === null ? "" : String(parsed.year),
    confirmedMonth: parsed === null ? "" : String(parsed.month),
    confirmedDay: parsed === null || parsed.day === null ? "" : String(parsed.day),
  };
}

export function InlineEditCell({
  row,
  field,
  label,
  onSaved,
  trailing,
  /** Ersetzt den angezeigten Text im Ruhezustand, z. B. geltendes Wahldatum. */
  displayOverride,
}: {
  row: MunicipalityRow;
  field: InlineField;
  label: string;
  onSaved?: () => void;
  /** z. B. das Herkunftszeichen des Wahldatums. */
  trailing?: React.ReactNode;
  displayOverride?: React.ReactNode;
}) {
  const saveFn = useServerFn(saveMunicipality);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => inlineInputValue(row, field));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Verhindert doppeltes Speichern (Eingabetaste und anschließendes Verlassen)
  // und Speichern nach Escape.
  const busyRef = useRef(false);
  const skipBlurRef = useRef(false);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const open = () => {
    skipBlurRef.current = false;
    setDraft(inlineInputValue(row, field));
    setError(null);
    setEditing(true);
  };

  /** Escape verwirft, ohne zu speichern. */
  const cancel = () => {
    setDraft(inlineInputValue(row, field));
    setError(null);
    setEditing(false);
  };

  const commit = async (input: string) => {
    if (busyRef.current) return;
    const next = inlineValues(row, field, input);
    if ("error" in next) {
      setError(next.error);
      toast.error(next.error);
      return;
    }
    busyRef.current = true;
    setSaving(true);
    setError(null);
    try {
      await saveFn({ data: { id: row.id, ...payloadFromValues(next) } });
      setEditing(false);
      toast.success(`${label} gespeichert.`);
      onSaved?.();
    } catch (saveError) {
      const message = (saveError as Error).message;
      setError(message);
      toast.error(message);
    } finally {
      busyRef.current = false;
      setSaving(false);
    }
  };

  /** Feld verlassen: geänderten Wert speichern, unveränderten einfach schließen. */
  const commitOnLeave = () => {
    if (skipBlurRef.current || busyRef.current) return;
    if (draft.trim() === inlineInputValue(row, field).trim()) {
      cancel();
      return;
    }
    void commit(draft);
  };

  if (editing && field === "runsAgain") {
    return (
      <div className="space-y-1">
        <select
          autoFocus
          aria-label={label}
          className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
          value={draft}
          disabled={saving}
          onChange={(event) => {
            setDraft(event.target.value);
            void commit(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              cancel();
            }
          }}
          onBlur={cancel}
        >
          {RUNS_AGAIN_VALUES.map((value) => (
            <option key={value} value={value}>
              {RUNS_AGAIN_LABELS[value]}
            </option>
          ))}
        </select>
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
      </div>
    );
  }

  if (editing) {
    return (
      <div className="space-y-1">
        <input
          ref={inputRef}
          aria-label={label}
          className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
          value={draft}
          disabled={saving}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void commit(draft);
            }
            if (event.key === "Escape") {
              event.preventDefault();
              skipBlurRef.current = true;
              cancel();
            }
          }}
          onBlur={commitOnLeave}
        />
        {error ? <p className="text-xs text-destructive">{error}</p> : null}
      </div>
    );
  }

  return (
    <span className="group inline-flex items-center gap-1">
      <span>{displayOverride ?? inlineDisplay(row, field)}</span>
      {trailing}
      <button
        type="button"
        aria-label={`${label} bearbeiten`}
        title={`${label} bearbeiten`}
        onClick={open}
        className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
      >
        <Pencil className="h-3.5 w-3.5 text-foreground/70" aria-hidden />
      </button>
    </span>
  );
}
